<div class="max-w-7xl mx-auto my-8 px-4">

    <!-- Back & Edit/Duplicate Actions -->
    <div class="w-full flex flex-row justify-between mt-8 mb-4">
        <app-btn-back routeTo=".." icon="fa fa-close" btnText="Cancel"></app-btn-back>
        @if ((myRoles$ | async)?.admin && group?.memberStatus == membershipStatus.active) {
        <div class="flex flex-row gap-2">
            <app-btn-route [btnText]="'Duplicate'" [icon]="'fa-regular fa-copy'" [routeTo]="'../create'"
                [queryParams]="{eventId:eventId}"
                [colors]="'bg-primary-500 active:bg-primary-300 hover:bg-primary-300 disabled:bg-primary-200 text-white'"></app-btn-route>
            <app-btn-route [routeTo]="'edit'" [btnText]="'Edit'" [icon]="'fa fa-edit'"
                [colors]="'bg-primary-500 active:bg-primary-300 hover:bg-primary-300 disabled:bg-primary-200 text-white'"></app-btn-route>
        </div>
        }
    </div>

    <!-- Title and Status Block -->
    <div class="w-full flex flex-col mb-4">
        <h1 class="font-semibold text-2xl capitalize text-primary-500 mb-2">{{event?.name}}</h1>
        <div class="flex flex-row gap-2 items-center">
            <span class="text-sm text-blueGray-400">Status:</span>
            @if (event?.status) {
            <app-chip [label]="(event?.status | titlecase) ?? ''" [color]="event?.status === eventStatus.active ? chipColor.SUCCESS :
                         event?.status === eventStatus.request ? chipColor.WARNING :
                         event?.status === eventStatus.cancelled ? chipColor.INFO :
                         event?.status === eventStatus.rejected ? chipColor.DANGER :
                         chipColor.PRIMARY"
                class="inline-block rounded-full cursor-pointer hover:opacity-80 active:opacity-60 transition-opacity"
                (click)="isAuthorized ? changeStatusDialog() : null">
            </app-chip>
            }
        </div>
    </div>

    <!-- 1st Full Width Row: Event Banner Image -->
    @if (event?.bannerImage) {
    <div class="w-full mb-4">
        <img alt="Event Image" [src]="event?.bannerImage"
            class="shadow-lg rounded-xl w-full object-cover border-none max-h-[350px] lg:max-h-[500px]" />
    </div>
    }

    <!-- 2nd Row: Divided Columns -->
    <div class="flex flex-col lg:flex-row gap-4">
        @if (group) {
        <!-- Column 1: Main Content Area -->
        <div class="w-full lg:w-7/12 flex flex-col gap-4">
            <!-- Consolidated Details Card -->
            <div class="bg-white border border-blueGray-200 rounded-xl p-6 shadow-sm flex flex-col gap-6">
                <!-- Description -->
                <div>
                    <label class="block uppercase text-blueGray-400 text-xs font-bold mb-3 tracking-wide">
                        Description
                    </label>
                    <p class="text-blueGray-700 text-sm whitespace-pre-line leading-relaxed">{{event?.description}}</p>
                </div>

                <!-- Event Sessions / Sub-Events -->
                @if (group && group.memberStatus === membershipStatus.active && event?.events) {
                <div class="border-t border-blueGray-200/60 pt-6">
                    <label class="block uppercase text-blueGray-400 text-xs font-bold mb-4 tracking-wide">
                        Event Sessions / Sub-Events
                    </label>
                    <div class="flex flex-col gap-3">
                        @for (subEvent of getSortedSubEvents(event?.events); track subEvent.id) {
                        <div (click)="isAdmin && subEvent.requestVolunteers ? openVolunteersDialog(subEvent) : null"
                            [class.cursor-pointer]="isAdmin && subEvent.requestVolunteers"
                            class="p-4 bg-white border border-blueGray-150 rounded-xl shadow-sm flex items-center justify-between gap-4">
                            <div class="flex flex-col items-start flex-1">
                                <span class="text-xs text-primary-300">
                                    {{ subEvent.date.toDate() | moment: 'ddd MMM DD, YYYY hh:mm a' }}
                                </span>
                                <span class="font-bold text-sm text-blueGray-800">{{ subEvent.name }}</span>
                            </div>
                            @if (subEvent.requestVolunteers) {
                            <div class="flex items-center gap-2">
                                <span
                                    class="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium flex items-center gap-1 shrink-0">
                                    <i class="fa fa-hand-holding-heart"></i> Volunteers
                                </span>
                                @if (isAdmin) {
                                <app-btn-fab (click)="openVolunteersDialog(subEvent); $event.stopPropagation()"
                                    icon="fa fa-users"
                                    bgColor="bg-blueGray-50 hover:bg-blueGray-100 border border-blueGray-200"
                                    textColor="text-primary-500" tooltip="View Volunteers" size="sm">
                                </app-btn-fab>
                                }
                            </div>
                            }
                        </div>
                        }
                    </div>
                </div>
                }

                <!-- Start & End Date Section -->
                <div class="flex flex-col md:flex-row gap-6 border-t border-blueGray-200/60 pt-6">
                    <div class="flex-1 flex items-start gap-3">
                        <div
                            class="w-10 h-10 flex items-center justify-center bg-blueGray-100 text-blueGray-600 rounded-lg shrink-0">
                            <i class="fa fa-calendar-alt text-base"></i>
                        </div>
                        <div class="flex flex-col">
                            <label class="block uppercase text-blueGray-400 text-xs font-bold tracking-wider mb-1">
                                Start Date & Time
                            </label>
                            <span class="text-blueGray-700 text-sm font-semibold">
                                {{event?.startDateTime!.toDate() | moment: 'ddd MMM DD, YYYY hh:mm a'}}
                            </span>
                        </div>
                    </div>
                    <div class="hidden md:block w-px bg-blueGray-200/60 self-stretch"></div>
                    <div class="flex-1 flex items-start gap-3">
                        <div
                            class="w-10 h-10 flex items-center justify-center bg-blueGray-100 text-blueGray-600 rounded-lg shrink-0">
                            <i class="fa fa-calendar-check text-base"></i>
                        </div>
                        <div class="flex flex-col">
                            <label class="block uppercase text-blueGray-400 text-xs font-bold tracking-wider mb-1">
                                End Date & Time
                            </label>
                            <span class="text-blueGray-700 text-sm font-semibold">
                                {{event?.endDateTime!.toDate() | moment: 'ddd MMM DD, YYYY hh:mm a'}}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Contacts Info -->
                @if (group.primaryContact?.contact || group.secondaryContact?.contact) {
                <div class="flex flex-col md:flex-row gap-6 border-t border-blueGray-200/60 pt-6">
                    @if (group.primaryContact?.contact) {
                    <div class="flex-1 flex items-start gap-3">
                        <div
                            class="w-10 h-10 flex items-center justify-center bg-blueGray-100 text-blueGray-600 rounded-lg shrink-0">
                            <i class="fa fa-phone text-base"></i>
                        </div>
                        <div class="flex flex-col">
                            <label class="block uppercase text-blueGray-400 text-xs font-bold tracking-wider mb-1">
                                Primary Contact
                            </label>
                            <span class="text-blueGray-700 text-sm font-semibold">
                                +{{group.primaryContact?.phoneCode}} {{group.primaryContact?.contact}}
                            </span>
                        </div>
                    </div>
                    }
                    @if (group.primaryContact?.contact && group.secondaryContact?.contact) {
                    <div class="hidden md:block w-px bg-blueGray-200/60 self-stretch"></div>
                    }
                    @if (group.secondaryContact?.contact) {
                    <div class="flex-1 flex items-start gap-3">
                        <div
                            class="w-10 h-10 flex items-center justify-center bg-blueGray-100 text-blueGray-600 rounded-lg shrink-0">
                            <i class="fa fa-phone text-base"></i>
                        </div>
                        <div class="flex flex-col">
                            <label class="block uppercase text-blueGray-400 text-xs font-bold tracking-wider mb-1">
                                Secondary Contact
                            </label>
                            <span class="text-blueGray-700 text-sm font-semibold">
                                +{{group.secondaryContact?.phoneCode}} {{group.secondaryContact?.contact}}
                            </span>
                        </div>
                    </div>
                    }
                </div>
                }

                <!-- Location Map -->
                @if (event?.location) {
                <div class="flex flex-col gap-4 border-t border-blueGray-200/60 pt-6">
                    <div class="flex items-start gap-3">
                        <div
                            class="w-10 h-10 flex items-center justify-center bg-blueGray-100 text-blueGray-600 rounded-lg shrink-0">
                            <i class="fa fa-map-marked-alt text-base"></i>
                        </div>
                        <div class="flex flex-col">
                            <label class="block uppercase text-blueGray-400 text-xs font-bold tracking-wider mb-0.5">
                                Event Location
                            </label>
                            <span class="text-blueGray-700 text-sm font-semibold leading-snug">
                                {{ event?.location?.name || 'Location Unavailable' }}
                            </span>
                        </div>
                    </div>
                    <div class="rounded-lg overflow-hidden w-full h-[28rem] border border-blueGray-200 shadow-inner">
                        <app-view-location [location]="event?.location" height="100%"></app-view-location>
                    </div>
                </div>
                }
            </div>

        </div>

        <!-- Column 2: RSVP Summary -->
        <div class="w-full lg:w-5/12 flex flex-col gap-4">
            <!-- RSVP Card Widget -->
            <div class="bg-white border border-blueGray-200 rounded-xl p-6 shadow-sm flex flex-col gap-4">
                <label class="block uppercase text-blueGray-400 text-xs font-bold tracking-wide">
                    RSVP Status
                </label>

                <!-- Attending Cards Grid (flex-col left for icon + status, right count aligned bottom) -->
                <div class="flex flex-row gap-2 w-full flex-nowrap">
                    <!-- Attending Card -->
                    <div
                        class="bg-emerald-50 border border-emerald-100 rounded-xl p-2 flex flex-col items-start justify-between h-24 w-full">
                        <div class="flex flex-col items-start justify-start h-full">
                            <span class="text-emerald-600 text-lg">
                                <i class="fa fa-check-circle"></i>
                            </span>
                            <span
                                class="text-[9px] uppercase font-bold text-emerald-700 tracking-wider mt-1.5 leading-none">Attending</span>
                        </div>
                        <span class="text-2xl font-black text-emerald-800 leading-none">{{ eventStats?.attendingCount ||
                            0 }}</span>
                    </div>

                    <!-- Tentative Card -->
                    <div
                        class="bg-amber-50 border border-amber-100 rounded-xl p-2 flex flex-col items-start justify-between h-24 w-full">
                        <div class="flex flex-col items-start justify-start h-full">
                            <span class="text-amber-600 text-lg">
                                <i class="fa fa-question-circle"></i>
                            </span>
                            <span
                                class="text-[9px] uppercase font-bold text-amber-700 tracking-wider mt-1.5 leading-none">Tentative</span>
                        </div>
                        <span class="text-2xl font-black text-amber-800 leading-none">{{ eventStats?.tentativeCount || 0
                            }}</span>
                    </div>

                    <!-- Declined Card -->
                    <div
                        class="bg-red-50 border border-red-100 rounded-xl p-2 flex flex-col items-start justify-between h-24 w-full">
                        <div class="flex flex-col items-start justify-start h-full">
                            <span class="text-red-600 text-lg">
                                <i class="fa fa-times-circle"></i>
                            </span>
                            <span
                                class="text-[9px] uppercase font-bold text-red-700 tracking-wider mt-1.5 leading-none">Not
                                Attending</span>
                        </div>
                        <span class="text-2xl font-black text-red-800 leading-none">{{ eventStats?.declinedCount || 0
                            }}</span>
                    </div>
                </div>

                <!-- RSVP Action Button -->
                <button (click)="openRsvpBottomSheet()"
                    class="w-full bg-primary-500 active:bg-primary-600 hover:bg-primary-400 text-white font-bold uppercase text-xs px-4 py-3 rounded-lg shadow-sm hover:shadow focus:outline-none transition-all duration-150 flex items-center justify-center gap-2">
                    <i class="fa fa-envelope-open-text text-sm"></i>
                    {{ userRsvpStatus ? 'RSVP: ' + (userRsvpStatus === 'declined' ? 'Not Attending' : (userRsvpStatus
                    === 'tentative' ? 'Tentative' : 'Attending')) : 'RSVP to Event' }}
                </button>
            </div>

            <!-- Volunteering Card Widget -->
            @if (group && group.memberStatus === membershipStatus.active && event?.events && hasVolunteeringSessions())
            {
            <div class="bg-white border border-blueGray-200 rounded-xl p-6 shadow-sm flex flex-col gap-4">
                <label class="block uppercase text-blueGray-400 text-xs font-bold tracking-wide">
                    Volunteering Tasks
                </label>
                <div class="flex flex-col gap-3">
                    @for (subEvent of getSortedSubEvents(event?.events); track subEvent.id) {
                    @if (subEvent.requestVolunteers) {
                    <div (click)="isAdmin ? openVolunteersDialog(subEvent) : null" [class.cursor-pointer]="isAdmin"
                        [ngClass]="hasUserVolunteered(subEvent.id) ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-blueGray-150'"
                        class="p-3 border rounded-xl shadow-sm flex flex-row items-stretch justify-between gap-3">
                        <div class="flex flex-col flex-1 justify-between">
                            <div class="flex flex-col">
                                <span class="text-xs text-primary-300">
                                    {{ subEvent.date.toDate() | moment: 'ddd MMM DD, h:mm a' }}
                                </span>
                                <span class="font-bold text-sm text-blueGray-800">{{ subEvent.name }}</span>
                            </div>
                            @if ((volunteeringDetails[subEvent.id] &&
                            volunteeringDetails[subEvent.id].volunteerCriteria) || subEvent.volunteerCriteria) {
                            <span class="text-blueGray-500 italic text-[11px] mt-1.5">
                                Criteria: {{ volunteeringDetails[subEvent.id] ?
                                volunteeringDetails[subEvent.id].volunteerCriteria : subEvent.volunteerCriteria }}
                            </span>
                            }
                        </div>

                        <!-- Right Actions Column (Button on top, vacancies count at bottom right) -->
                        <div class="flex flex-col items-end justify-between shrink-0">
                            <!-- Volunteering Button -->
                            <div class="flex items-center gap-2">
                                @if (isAdmin) {
                                <app-btn-fab (click)="openVolunteersDialog(subEvent); $event.stopPropagation()"
                                    icon="fa fa-users"
                                    bgColor="bg-blueGray-50 hover:bg-blueGray-100 border border-blueGray-200"
                                    textColor="text-primary-500" tooltip="View Volunteers" size="sm">
                                </app-btn-fab>
                                }
                                @if (volunteeringProgress[subEvent.id]) {
                                <!-- In-progress spinner -->
                                <app-btn-fab [isDisabled]="true" icon="fa fa-spinner fa-spin"
                                    (click)="$event.stopPropagation()"
                                    bgColor="bg-blueGray-50 border border-blueGray-100" textColor="text-blueGray-400"
                                    tooltip="Saving..." size="sm">
                                </app-btn-fab>
                                } @else {
                                @if (hasUserVolunteered(subEvent.id)) {
                                <!-- Cancel button for already volunteered tasks -->
                                <app-btn-fab (click)="unvolunteer(subEvent.id); $event.stopPropagation()"
                                    icon="fa fa-times" bgColor="bg-blueGray-50 border border-blueGray-200"
                                    textColor="text-red-500" tooltip="Unvolunteer" class="volunteer-checked-btn"
                                    size="sm">
                                </app-btn-fab>
                                } @else if (volunteeringDetails[subEvent.id] ?
                                (volunteeringDetails[subEvent.id].volunteersRequired -
                                volunteeringDetails[subEvent.id].volunteers.length > 0) : (subEvent.volunteersRequired
                                || 0)
                                > 0) {
                                <!-- Light gray checkmark box to volunteer -->
                                <app-btn-fab (click)="volunteer(subEvent.id); $event.stopPropagation()"
                                    icon="fa fa-check"
                                    bgColor="bg-blueGray-50 hover:bg-emerald-50 border border-blueGray-200"
                                    textColor="text-blueGray-400 hover:text-emerald-600" tooltip="Volunteer" size="sm">
                                </app-btn-fab>
                                } @else {
                                <!-- Full/No vacancies checkmark box, disabled -->
                                <app-btn-fab [isDisabled]="true" icon="fa fa-lock" (click)="$event.stopPropagation()"
                                    bgColor="bg-blueGray-50 border border-blueGray-100" textColor="text-blueGray-300"
                                    tooltip="Full" size="sm">
                                </app-btn-fab>
                                }
                                }
                            </div>

                            <!-- Vacancies count formatted as x/y -->
                            <span class="text-xs text-emerald-600 font-semibold mt-auto pt-2">
                                {{ volunteeringDetails[subEvent.id] ?
                                volunteeringDetails[subEvent.id].volunteers.length : 0 }}/{{
                                volunteeringDetails[subEvent.id] ?
                                volunteeringDetails[subEvent.id].volunteersRequired : (subEvent.volunteersRequired
                                || 0) }}
                            </span>
                        </div>
                    </div>
                    }
                    }
                </div>
            </div>
            }
        </div>
        }
    </div>

    <!-- Centered RSVP Dialog Overlay with Backdrop Blur -->
    @if (showRsvpBottomSheet) {
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity"
        (click)="closeRsvpBottomSheet()">
        <div class="relative w-full max-w-md bg-white rounded-2xl p-6 shadow-2xl m-4"
            (click)="$event.stopPropagation()">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
                <h3 class="text-lg font-bold text-blueGray-800">RSVP to Event</h3>
                <button (click)="closeRsvpBottomSheet()"
                    class="text-blueGray-400 hover:text-blueGray-600 focus:outline-none">
                    <i class="fa fa-times text-lg"></i>
                </button>
            </div>

            <p class="text-sm text-blueGray-500 mb-6 font-medium">Please select your RSVP status:</p>

            <!-- Options -->
            <div class="flex justify-between gap-4 mb-6">
                <!-- Attending -->
                <button (click)="selectTempStatus('attending')"
                    [ngClass]="tempRsvpStatus === 'attending' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-emerald-500 border-blueGray-200 hover:bg-emerald-50/50'"
                    class="flex-1 flex flex-col items-center p-4 border rounded-xl transition-all focus:outline-none">
                    <i class="fa fa-check-circle text-3xl mb-2"></i>
                    <span class="text-xs font-bold">Attending</span>
                </button>

                <!-- Tentative -->
                <button (click)="selectTempStatus('tentative')"
                    [ngClass]="tempRsvpStatus === 'tentative' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-500 border-blueGray-200 hover:bg-amber-50/50'"
                    class="flex-1 flex flex-col items-center p-4 border rounded-xl transition-all focus:outline-none">
                    <i class="fa fa-question-circle text-3xl mb-2"></i>
                    <span class="text-xs font-bold">Tentative</span>
                </button>

                <!-- Declined (Not Attending) -->
                <button (click)="selectTempStatus('declined')"
                    [ngClass]="tempRsvpStatus === 'declined' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-500 border-blueGray-200 hover:bg-red-50/50'"
                    class="flex-1 flex flex-col items-center p-4 border rounded-xl transition-all focus:outline-none">
                    <i class="fa fa-times-circle text-3xl mb-2"></i>
                    <span class="text-xs font-bold">Not Attending</span>
                </button>
            </div>

            <!-- Additional Guests Counter with transition -->
            <div [ngClass]="tempRsvpStatus === 'attending' ? 'max-h-28 opacity-100 mb-4 mt-2' : 'max-h-0 opacity-0 mb-0 mt-0 pointer-events-none'"
                class="transition-all duration-300 ease-in-out overflow-hidden">
                <div
                    class="bg-blueGray-50 rounded-xl p-4 border border-blueGray-100 flex flex-row items-center justify-between">
                    <div class="flex flex-col">
                        <span class="text-sm font-bold text-blueGray-700">Additional Guests</span>
                        <span class="text-xs text-blueGray-500">Bringing family or friends?</span>
                    </div>
                    <div
                        class="flex items-center gap-4 bg-white border border-blueGray-200 rounded-lg p-1.5 shadow-inner">
                        <button (click)="adjustTempGuests(-1)" [disabled]="tempRsvpGuests <= 0"
                            class="w-8 h-8 rounded-md bg-blueGray-100 hover:bg-blueGray-200 text-blueGray-600 flex items-center justify-center font-black disabled:opacity-50 transition-all focus:outline-none">
                            <i class="fa fa-minus text-xs"></i>
                        </button>
                        <span class="font-bold text-blueGray-800 text-base min-w-[20px] text-center">{{ tempRsvpGuests
                            }}</span>
                        <button (click)="adjustTempGuests(1)" [disabled]="tempRsvpGuests >= 10"
                            class="w-8 h-8 rounded-md bg-blueGray-100 hover:bg-blueGray-200 text-blueGray-600 flex items-center justify-center font-black disabled:opacity-50 transition-all focus:outline-none">
                            <i class="fa fa-plus text-xs"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Bottom Buttons Row (Cancel & Confirm) -->
            <div class="mt-6 flex justify-end gap-3 border-t border-blueGray-100 pt-4">
                <button type="button" (click)="closeRsvpBottomSheet()"
                    class="text-blueGray-500 hover:text-blueGray-700 bg-transparent font-bold uppercase text-xs px-6 py-3 rounded-lg focus:outline-none transition-all duration-150">
                    Cancel
                </button>
                <button type="button" (click)="updateRsvp(tempRsvpStatus, tempRsvpGuests)" [disabled]="!tempRsvpStatus"
                    class="bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white font-bold uppercase text-xs px-6 py-3 rounded-lg shadow-sm hover:shadow focus:outline-none transition-all duration-150 disabled:opacity-50 disabled:pointer-events-none">
                    Confirm
                </button>
            </div>
        </div>
    </div>
    }
</div>